import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import useCountriesStore from "store/zustand/countries";
import PhoneNumberInput from "components/common/PhoneInput";
import {
  TextField,
  Box,
  Typography,
  Grid,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { getCitiesByCountry } from "store/services/countries.service";

const CompanyInfo = () => {
  const {
    register,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();

  const watchCountry = watch("country");
  const { list: countries } = useCountriesStore(); // Assume countries list is stored in zustand
  const [selectedCountry, setSelectedCountry] = useState(watchCountry || ""); // Store the selected country
  const [cities, setCities] = useState([]); // Store the list of cities
  const [loadingCities, setLoadingCities] = useState(false); // For loading state

  // Handle country change
  const handleCountryChange = useCallback(
    async (countryId) => {
      setSelectedCountry(countryId); // Set selected country
      setValue("country", countryId);
      if (countryId) {
        setLoadingCities(true); // Show loading state
        setValue("city", "");

        try {
          // Call API to fetch cities based on the selected country
          const citiesData = await getCitiesByCountry(countryId);
          setCities(citiesData?.data?.data?.[0]?.cities || []); // Set the fetched cities
        } catch (error) {
          console.error("Error fetching cities:", error);
        } finally {
          setLoadingCities(false); // Hide loading state
        }
      } else {
        setCities([]); // Clear cities if no country is selected
      }
    },
    [setValue]
  );
  useEffect(() => {
    handleCountryChange(watchCountry);
  }, [watchCountry, handleCountryChange]);

  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6">
        {t("auth.signup.sections.companyInfo")}
      </Typography>
      <Paper elevation={1} sx={{ bgcolor: "transparent", p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Company Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.companyName.label")}
              fullWidth
              {...register("company_name", {
                required: t("auth.signup.fields.companyName.required"),
              })}
              error={Boolean(errors.company_name)}
              helperText={errors.company_name?.message}
            />
          </Grid>

          {/* Arabic Legal Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.companyLegalNameArabic.label")}
              fullWidth
              {...register("company_legal_name_arabic", {
                required: t(
                  "auth.signup.fields.companyLegalNameArabic.required"
                ),
              })}
              error={Boolean(errors.company_legal_name_arabic)}
              helperText={errors.company_legal_name_arabic?.message}
            />
          </Grid>

          {/* Legal Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.companyLegalName.label")}
              fullWidth
              {...register("company_legal_name", {
                required: t("auth.signup.fields.companyLegalName.required"),
              })}
              error={Boolean(errors.company_legal_name)}
              helperText={errors.company_legal_name?.message}
            />
          </Grid>

          {/* MOI Number */}
          <Grid size={{ xs: 12, md: 6 }}>
            <PhoneNumberInput
              required={true}
              field={{
                ...register("moi_number", {
                  required: t("auth.common.phone_number_required"),
                  //   pattern: {
                  //     value: /^[0-9]{10}$/, // Example pattern, adjust based on your region
                  //     message: "Invalid mobile number",
                  //   },
                }),
                value: watch("moi_number") || "",
              }}
              error={errors.moi_number?.message}
              onChange={(e) => setValue("moi_number", e)} // Proper onChange handler to update value
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t("auth.signup.fields.country.label")}</InputLabel>
              <Select
                value={selectedCountry}
                {...register("country", {
                  required: t("auth.signup.fields.country.required"),
                })}
                onChange={(e) => handleCountryChange(e.target.value)}
                label={t("auth.signup.fields.country.label")}
                error={Boolean(errors.country)}
              >
                {countries?.map((country) => (
                  <MenuItem key={country.value} value={country.label}>
                    <Stack direction={"row"} gap={1} alignItems="center">
                      <Box
                        component="img"
                        src={country?.flag}
                        alt={country?.label}
                        sx={{ width: "24px", height: "18px" }}
                      />
                      <Typography>{country.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              {errors.country && (
                <Typography color="error">{errors.country.message}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t("auth.signup.fields.city.label")}</InputLabel>
              <Select
                value={watch("city") || ""}
                {...register("city", {
                  required: t("auth.signup.fields.city.required"),
                })}
                error={Boolean(errors.city)}
                disabled={loadingCities || !selectedCountry}
                label={t("auth.signup.fields.city.label")}
              >
                {loadingCities ? (
                  <MenuItem disabled>{t("auth.common.loadingCities")}</MenuItem>
                ) : (
                  cities?.map((city) => (
                    <MenuItem key={city.value} value={city.label}>
                      {city.label}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.city && (
                <Typography color="error">{errors.city.message}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.address.label")}
              fullWidth
              {...register("address", {
                required: t("auth.signup.fields.address.required"),
              })}
              error={Boolean(errors.address)}
              helperText={errors.address?.message}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.nationalAddress.label")}
              fullWidth
              {...register("nationalAddress", {
                required: t("auth.signup.fields.nationalAddress.required"),
                pattern: {
                  value: /^[A-Z]{4}\d{4}$/,
                  message: t(
                    "createOrder.customerDialog.nationalAddressInvalid",
                    {
                      defaultValue:
                        "National address must be 4 uppercase letters followed by 4 digits",
                    }
                  ),
                },
                minLength: {
                  value: 8,
                  message: t(
                    "createOrder.customerDialog.nationalAddressLength",
                    {
                      defaultValue: "National address must be 8 characters",
                    }
                  ),
                },
                maxLength: {
                  value: 8,
                  message: t(
                    "createOrder.customerDialog.nationalAddressLength",
                    {
                      defaultValue: "National address must be 8 characters",
                    }
                  ),
                },
              })}
              error={Boolean(errors.nationalAddress)}
              helperText={errors.nationalAddress?.message}
            />
          </Grid>
        </Grid>
      </Paper>
      <Typography variant="h6">
        {t("auth.signup.sections.ownerDetails")}
      </Typography>
      <Paper elevation={1} sx={{ bgcolor: "transparent", p: 2, my: 2 }}>
        <Grid container spacing={2}>
          {/* Company Owner's First Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.ownerFirstName.label")}
              fullWidth
              {...register("company_owner_first_name", {
                required: t("auth.signup.fields.ownerFirstName.required"),
              })}
              error={Boolean(errors.company_owner_first_name)}
              helperText={errors.company_owner_first_name?.message}
            />
          </Grid>

          {/* Company Owner's Last Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.ownerLastName.label")}
              fullWidth
              {...register("company_owner_last_name", {
                required: t("auth.signup.fields.ownerLastName.required"),
              })}
              error={Boolean(errors.company_owner_last_name)}
              helperText={errors.company_owner_last_name?.message}
            />
          </Grid>

          {/* Owner's Email */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.ownerEmail.label")}
              fullWidth
              {...register("company_owner_email", {
                required: t("auth.signup.fields.ownerEmail.required"),
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: t("auth.signup.fields.ownerEmail.invalid"),
                },
              })}
              error={Boolean(errors.company_owner_email)}
              helperText={errors.company_owner_email?.message}
            />
          </Grid>

          {/* Owner's Mobile Number */}
          <Grid size={{ xs: 12, md: 6 }}>
            <PhoneNumberInput
              required={true}
              field={{
                ...register("company_owner_mobile", {
                  required: t("auth.common.phone_number_required"),
                  //   pattern: {
                  //     value: /^[0-9]{10}$/, // Example pattern, adjust based on your region
                  //     message: "Invalid mobile number",
                  //   },
                }),
                value: watch("company_owner_mobile") || "",
              }}
              error={errors.company_owner_mobile?.message}
              onChange={(e) => setValue("company_owner_mobile", e)} // Proper onChange handler to update value
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CompanyInfo;
